'use client'

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getBlogById } from "../redux/blogSlice";  // Assuming this is your Redux slice

// BlogDetails Component
const BlogDetails = () => {
  const { id } = useParams();  // Get the blog ID from URL params
  const dispatch = useDispatch();
  const { blog, loading, error } = useSelector((state) => state.blogs);  // Assuming you have loading and error in your blog slice

  useEffect(() => {
    dispatch(getBlogById(id));  // Fetch blog details based on the ID
  }, [dispatch, id]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading blog</div>;
  if (!blog) return <div>No blog found</div>;

  const reviews = {
    average: 4,
    featured: [
      {
        id: 1,
        rating: 5,
        content: blog.content,  // Assuming content is a string here
        date: new Date(blog.created_at).toLocaleDateString(),
        datetime: blog.created_at,
        author: "Anonymous",  // Replace with actual author
        avatarSrc: "https://example.com/avatar.jpg",  // Replace with actual avatar URL
      },
    ],
  };

  const categories = [
    { name: "Technology", count: 12 },
    { name: "Design", count: 8 },
    { name: "Business", count: 15 },
    { name: "Marketing", count: 10 }
  ]

  const archives = [
    { month: "December 2023", count: 5 },
    { month: "November 2023", count: 8 },
    { month: "October 2023", count: 12 }
  ] // Dynamic archives from blog data

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-[300px] bg-gray-900">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: `url('/blogs/${blog.blog_image}')` }}
        />
        <div className="relative flex items-center justify-center h-full">
          <h1 className="text-4xl font-bold text-white">Blog Single</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Blog Content */}
        <main className="lg:col-span-8">
          <article className="bg-white rounded-lg shadow-sm">
            <img
              src={`/blogs/${blog.blog_image}`}  // Dynamic image URL
              alt="Blog cover"
              className="w-full h-[400px] object-cover rounded-t-lg"
            />

            <div className="p-8">
              {/* Social Share Buttons */}
              <div className="flex flex-col gap-2 float-left mr-6">
                <button className="rounded-full bg-blue-500 text-white p-2">
                  f
                </button>
                <button className="rounded-full bg-sky-500 text-white p-2">
                  t
                </button>
                <button className="rounded-full bg-red-500 text-white p-2">
                  in
                </button>
              </div>

              {/* Blog Content */}
              <div>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span>By: {blog.author_id || "Anonymous"}</span>
                  <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                  <span>{blog.category}</span>
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-6">{blog.title}</h1>

                <div className="prose max-w-none">
                  <p className="text-gray-600 leading-relaxed">{blog.blog_excerpt}</p>
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                  className="text-gray-800 text-base leading-relaxed whitespace-pre-line"
                />
              </div>
            </div>
          </article>
        </main>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-8">
          {/* Search */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="font-semibold text-lg mb-4">Search</h2>
            <div className="relative">
              <input
                type="search"
                placeholder="Search posts..."
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="font-semibold text-lg mb-4">Categories</h2>
            <ul className="space-y-2">
              {categories.map((category) => (
                <li key={category.name} className="flex justify-between items-center text-gray-600 hover:text-gray-900">
                  <a href="#">{category.name}</a>
                  <span className="text-gray-400">({category.count})</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Archives */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="font-semibold text-lg mb-4">Archives</h2>
            <ul className="space-y-2">
              {archives.map((archive) => (
                <li key={archive.month} className="flex justify-between items-center text-gray-600 hover:text-gray-900">
                  <a href="#">{archive.month}</a>
                  <span className="text-gray-400">({archive.count})</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Tags */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="font-semibold text-lg mb-4">Tags</h2>
            <div className="flex flex-wrap gap-2">
              {['Design', 'Development', 'UI/UX', 'Web', 'App'].map((tag) => (
                <button key={tag} className="border border-gray-300 text-gray-600 py-1 px-4 rounded-full text-sm">
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogDetails;
